<script setup>
  import {computed, defineAsyncComponent, inject, onMounted, ref, watch} from "vue";

  import ComponentBreadcrumb from "@components/Breadcrumb/Breadcrumb.vue";
  import ComponentTabButton from "@components/Buttons/TabButton.vue";
  import ComponentConfirmationDialog from "@components/Dialogs/ConfirmationDialog.vue";
  import ComponentPatientNoteDialog from "@components/Dialogs/PatientNoteDialog.vue";
  import ComponentPatientTodosDialog from "@components/Dialogs/PatientTodosDialog.vue";
  import ComponentEmptyState from "@components/EmptyState/EmptyState.vue";
  import ComponentFilterFlags from "@components/Filter/FilterFlags.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import ComponentPagination from "@components/Pagination/Pagination.vue";
  import {router, useForm, usePage} from "@inertiajs/vue3";
  import PatientsPatientHead from "@pages/Patients/Components/PatientHead.vue";
  import PatientsPatientRow from "@pages/Patients/Components/PatientRow.vue";
  import {MedButton, MedSpinner} from "@pharma4u/medicheck-components";
  import {debounce} from "es-toolkit";

  defineOptions({
    name: "PagesPatientIndex",
  });

  const props = defineProps({
    patients: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
    flagContacts: {
      type: Object,
      default: () => {
        return {};
      },
    },
  });

  const PatientsExternalPatientHead = defineAsyncComponent(
    () => import("@pages/Patients/Components/ExternalPatientHead.vue"),
  );
  const PatientsExternalPatientRow = defineAsyncComponent(
    () => import("@pages/Patients/Components/ExternalPatientRow.vue"),
  );
  const PatientsDeletedPatientHead = defineAsyncComponent(
    () => import("@pages/Patients/Components/DeletedPatientHead.vue"),
  );
  const PatientsDeletedPatientRow = defineAsyncComponent(
    () => import("@pages/Patients/Components/DeletedPatientRow.vue"),
  );

  const privacy = inject("$privacy");

  const page = usePage();

  const confirmDialogTitle = ref("");
  const confirmDialogContent = ref("");

  const refConfirmBulkDeleteDialog = ref(null);
  const refConfirmBulkForceDeleteDialog = ref(null);
  const refConfirmBulkRecoverDialog = ref(null);
  const refPatientNoteDialog = ref(null);
  const refPatientTodosDialog = ref(null);

  const filteredPatients = ref([]);

  const selectedRows = ref([]);

  const availableRows = computed(() => {
    return filteredPatients.value.map((patient) => patient.id);
  });

  let form = useForm({
    order:
      page.props?.filters?.order ||
      (page.props?.filters?.filter === "deleted-patients" ? "-deleted_at" : "-updated_at"), // default order depends on active tab
    filter: typeof page.props?.filters?.filter === "function" ? null : page.props?.filters?.filter,
    q: {
      search: page.props?.filters?.q?.search || null,
    },
    flags: page.props?.filters?.flags || [],
  });

  onMounted(() => {
    filterPatients();
  });

  const availableFlags = computed(() => {
    if (form.filter === "shared-with-me") {
      return {
        "status-shared-with-me": {
          label: "Medikationsanalysen",
          nullable: false,
          options: {
            inProgress: "in Bearbeitung",
            completed: "abgeschlossen",
            discarded: "als irrelevant markiert",
          },
        },
        "shared-by-me": "enthält geteilte Analysen",
        "nursing-home": "ist Heimpatient",
        "open-todos": "enthält offene Aufgaben",
        "missing-activity": "inaktiv seit 1 Jahr",
      };
    }
    if (form.filter === "deleted-patients") {
      return {};
    }

    if (page.props.client.license.is_core) {
      return {
        "nursing-home": "ist Heimpatient",
        "related-contact": {
          label: "mit Kontakt",
          options: props.flagContacts,
        },
        "missing-activity": "inaktiv seit 1 Jahr",
      };
    }

    return {
      status: {
        label: "Medikationsanalysen",
        nullable: false,
        options: {
          inProgress: "in Bearbeitung",
          "service-recommended": "erneute Leistungserbringung möglich",
          completed: "abgeschlossen",
          discarded: "als irrelevant markiert",
        },
      },
      "shared-by-me": "enthält geteilte Analysen",
      "nursing-home": "ist Heimpatient",
      "from-wawi": "aus Warenwirtschaft",
      "related-contact": {
        label: "mit Kontakt",
        options: props.flagContacts,
      },
      "open-todos": "enthält offene Aufgaben",
      privacy: {
        label: "Datenschutz",
        nullable: false,
        options: {
          "missing-agreementpdlconfirmed": "fehlende Kurzvereinbarung zur pDL",
          "missing-releasefromconfidentialityconfirmed": "fehlende Schweigepflichtentbindung",
          "missing-datasecurityconfirmed": "fehlende Einwilligung Medikationsanalyse",
          "missing-datasecurityresearchconfirmed": "fehlende Einwilligung Forschung",
          "missing-datasecurityaiconfirmed": "fehlende Einwilligung KI",
          "missing-activity": "inaktiv seit 1 Jahr",
        },
      },
    };
  });

  watch(
    () => form.q.search,
    debounce(function () {
      submitFilter();
    }, 500),
  );

  watch(
    () => form.flags,
    debounce(function () {
      submitFilter();
    }, 500),
    {deep: true}, // required when watching arrays
  );

  watch(
    () => form.order,
    debounce(function () {
      submitFilter();
    }, 500),
  );

  watch(
    () => props.patients,
    () => {
      filterPatients();
    },
  );

  function submitFilter() {
    form.get(route("patients.index"), {preserveState: true, preserveScroll: true, replace: true});
  }

  function filterPatients() {
    privacy.whenCryptReady(async () => {
      const encryptedPatients = page.props.patients.data ?? [];

      const decryptedPatients = await Promise.all(encryptedPatients.map((p) => privacy.decryptPatient(p)));

      const words = (form?.q?.search?.toLowerCase().replace(",", " ") || "")
        .split(" ")
        .filter((word) => word.trim() !== "")
        .map((word) => {
          if (/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.test(word)) {
            // convert searchword "1.9.1990" to "01.09.1980"
            const [day, month, year] = word.split(".");
            return day.padStart(2, "0") + "." + month.padStart(2, "0") + "." + year;
          }
          return word;
        });

      if (words.length === 0) {
        filteredPatients.value = decryptedPatients;
        return;
      }

      filteredPatients.value = decryptedPatients.filter((patient) => {
        return words.every((word) => {
          return (
            (patient.lastname && patient.lastname.toLowerCase().includes(word.toLowerCase())) ||
            (patient.firstname && patient.firstname.toLowerCase().includes(word.toLowerCase())) ||
            (patient.birthdate_formatted && patient.birthdate_formatted.includes(word.toLowerCase())) ||
            (patient.insurancenumber && patient.insurancenumber.toLowerCase().startsWith(word.toLowerCase())) ||
            (patient.aliasname && patient.aliasname.toLowerCase().includes(word.toLowerCase())) ||
            // optional filter in "external patients" tab
            (form.filter === "shared-with-me" &&
              patient?.shared_owners[0] &&
              patient?.shared_owners[0]?.company &&
              patient?.shared_owners[0]?.company.toLowerCase().includes(word.toLowerCase())) ||
            (form.filter === "shared-with-me" &&
              patient?.shared_owners[0] &&
              patient?.shared_owners[0]?.name &&
              patient?.shared_owners[0]?.name.toLowerCase().includes(word.toLowerCase()))
          );
        });
      });
    });
  }

  function openPatientNote(patient) {
    refPatientNoteDialog.value.open(patient);
  }

  function openPatientTodos(patient) {
    refPatientTodosDialog.value.open(patient);
  }

  function confirmBulkForceDelete(patientIds) {
    confirmDialogTitle.value = "Patienten endgültig löschen?"; // << correct for 1 and more (Akkusativ)
    confirmDialogContent.value =
      patientIds.length === 1
        ? "Die Patientenakte wird mit allen enthaltenen Daten endgültig und unwiederbringlich gelöscht."
        : "Die Patientenakten (" +
          patientIds.length +
          ") werden mit allen enthaltenen Daten endgültig und unwiederbringlich gelöscht.";
    refConfirmBulkForceDeleteDialog.value.open(patientIds);
  }

  function optimisticRemovePatients(pageProperties, patientIds) {
    return {
      patients: {
        ...pageProperties.patients,
        data: pageProperties.patients.data.filter((p) => !patientIds.includes(p.id)),
      },
    };
  }

  function bulkForceRemove(patientIds) {
    selectedRows.value = [];

    router.delete(route("patients.destroy", {action: "forceDelete"}), {
      preserveScroll: true,
      data: {ids: patientIds},
      optimistic: (pageProperties) => optimisticRemovePatients(pageProperties, patientIds),
    });
  }

  function confirmBulkDelete(patientIds) {
    // only allow force deletion when using connect-core
    if (page.props?.client?.license?.license_name === "connect-core") {
      confirmBulkForceDelete(patientIds);
      return;
    }

    confirmDialogTitle.value = "Patienten löschen?"; // << correct for 1 and more (Akkusativ)
    confirmDialogContent.value =
      patientIds.length === 1
        ? "Die Patientenakte wird mit allen zugehörigen Medikationsanalysen in „Gelöschte Patienten“ verschoben."
        : "Die Patientenakten (" +
          patientIds.length +
          ") werden mit allen zugehörigen Medikationsanalysen in „Gelöschte Patienten“ verschoben.";
    refConfirmBulkDeleteDialog.value.open(patientIds);
  }

  function bulkRemove(patientIds) {
    selectedRows.value = [];

    router.delete(route("patients.destroy"), {
      preserveScroll: true,
      data: {ids: patientIds},
      optimistic: (pageProperties) => optimisticRemovePatients(pageProperties, patientIds),
    });
  }

  function confirmBulkRecover(patientIds) {
    confirmDialogTitle.value = "Patienten wiederherstellen?"; // <<script correct for 1 and more (Akkusativ)
    confirmDialogContent.value =
      patientIds.length === 1
        ? "Die Patientenakte wird mit allen Inhalten wiederhergestellt."
        : "Die Patientenakten (" + patientIds.length + ") werden mit allen Inhalten wiederhergestellt.";
    refConfirmBulkRecoverDialog.value.open(patientIds);
  }

  function bulkRecover(patientIds) {
    selectedRows.value = [];

    router.post(
      route("patients.restore"),
      {ids: patientIds},
      {
        preserveScroll: true,
        optimistic: (pageProperties) => optimisticRemovePatients(pageProperties, patientIds),
      },
    );
  }
</script>

<template>
  <ComponentBreadcrumb class="mt-2" :links="[{label: 'Patienten'}]" />

  <section class="mt-6">
    <div
      class="md:space-y-none mx:space-y-0 flex flex-col-reverse justify-between space-y-4 space-y-reverse md:flex-row"
    >
      <div class="flex space-x-2">
        <ComponentTabButton
          :active="form.filter !== 'shared-with-me' && form.filter !== 'deleted-patients'"
          :href="$route('patients.index')"
          :test-id="getTestId('my-patients')"
        >
          Meine Patienten
        </ComponentTabButton>

        <ComponentTabButton
          v-if="$page.props.can.view_shared_records"
          :active="form.filter === 'shared-with-me'"
          :href="$route('patients.index', {filter: 'shared-with-me'})"
          :test-id="getTestId('shared-with-me')"
        >
          Externe Patienten
        </ComponentTabButton>

        <ComponentTabButton
          v-if="
            $page.props?.client?.license?.license_name !== 'connect-core' &&
            ($page.props.can.delete_patients || $page.props.can.delete_wawi_patients)
          "
          :active="form.filter === 'deleted-patients'"
          :href="$route('patients.index', {filter: 'deleted-patients'})"
          :test-id="getTestId('deleted')"
        >
          Gelöschte Patienten
        </ComponentTabButton>
      </div>

      <!-- TODO did it change the height?-->
      <template v-if="$page.props.can.create_patients && form.filter === null">
        <MedButton
          variant="primary"
          as="inertia"
          icon="person_add"
          class="my-auto h-fit"
          :href="$route('patients.create')"
          :data-test="getTestId('create-patient-button')"
        >
          Neuer Patient
        </MedButton>
      </template>
    </div>

    <div class="bg-white p-4 shadow-sm">
      <!-- filter :: start -->
      <form :data-test="getTestId('form')" @submit.prevent="submitFilter">
        <div class="flex items-baseline justify-between space-x-8">
          <div>
            <p v-if="form.filter === 'deleted-patients'" class="mb-2">
              Gelöschte Patienten werden automatisch nach 30 Tagen gelöscht.
            </p>

            <div>
              <ComponentInput
                id="search-input"
                v-model.trim="form.q.search"
                autocomplete="off"
                class="max-w-72 lg:w-72!"
                placeholder="Suchen"
                :helper-text="
                  form.filter === 'shared-with-me'
                    ? 'nach Geburtsjahr, Alias, Herkunft'
                    : 'nach Name, Geburtsdatum, Versichertennummer'
                "
                :test-id="getTestId('search')"
              >
                <template #icon>
                  <ComponentIcon class="text-xl text-gray-600">search</ComponentIcon>
                </template>
              </ComponentInput>
            </div>
          </div>

          <ComponentFilterFlags
            v-if="Object.keys(availableFlags).length > 0"
            v-model="form.flags"
            :available-flags="availableFlags"
            :test-id="getTestId()"
          />
        </div>
      </form>

      <!-- ☑️ multiselect actions -->
      <section v-if="selectedRows?.length" class="items-top mt-4 -mb-4 flex space-x-1">
        <div class="my-2 rounded border-2 border-transparent bg-gray-200 px-1 py-0 text-xs font-semibold text-gray-700">
          {{ selectedRows?.length }}
        </div>

        <div class="flex flex-wrap gap-0.5">
          <template v-if="form.filter === 'deleted-patients'">
            <MedButton
              flat
              icon="sync"
              :data-test="getTestId('deleted-head-bulk-recover-button')"
              @click="confirmBulkRecover(selectedRows)"
            >
              Wiederherstellen
            </MedButton>
          </template>

          <template v-if="form.filter === 'shared-with-me' || form.filter === 'deleted-patients'">
            <MedButton
              flat
              icon="delete"
              :data-test="getTestId('default-head-bulk-delete-button')"
              @click="confirmBulkForceDelete(selectedRows)"
            >
              Löschen
            </MedButton>
          </template>

          <template v-else>
            <MedButton
              flat
              icon="delete"
              :data-test="getTestId('default-head-bulk-delete-button')"
              @click="confirmBulkDelete(selectedRows)"
            >
              Löschen
            </MedButton>
          </template>
        </div>
      </section>
      <!-- filter :: end -->

      <!-- table listing :: start -->
      <article class="mt-4">
        <table v-if="filteredPatients.length > 0" class="w-full table-auto">
          <thead>
            <PatientsExternalPatientHead
              v-if="form.filter === 'shared-with-me'"
              v-model="form.order"
              :available-rows="availableRows"
              :selected-rows="selectedRows"
              :test-id="getTestId()"
              @update:selected-rows="(newSelectedRows) => (selectedRows = newSelectedRows)"
            />

            <PatientsDeletedPatientHead
              v-else-if="form.filter === 'deleted-patients'"
              v-model="form.order"
              :available-rows="availableRows"
              :selected-rows="selectedRows"
              :test-id="getTestId()"
              @update:selected-rows="(newSelectedRows) => (selectedRows = newSelectedRows)"
            />

            <PatientsPatientHead
              v-else
              v-model="form.order"
              :available-rows="availableRows"
              :selected-rows="selectedRows"
              :test-id="getTestId()"
              @update:selected-rows="(newSelectedRows) => (selectedRows = newSelectedRows)"
            />
          </thead>

          <tbody>
            <template v-if="form.processing">
              <tr>
                <td colspan="5">
                  <div class="grid min-h-32 w-full place-content-center">
                    <MedSpinner size="3xl" :data-test="getTestId('patients-index-spinner')" />
                  </div>
                </td>
              </tr>
            </template>

            <template v-else-if="form.filter === 'shared-with-me'">
              <PatientsExternalPatientRow
                v-for="(patient, index) in filteredPatients"
                :key="patient.id"
                :patient="patient"
                :selected-rows="selectedRows"
                :test-id="getTestId(`${index}`)"
                @update:selected-rows="(newSelectedRows) => (selectedRows = newSelectedRows)"
                @open:patient-note="openPatientNote(patient)"
                @open:patient-todos="openPatientTodos(patient)"
                @confirm:delete="() => confirmBulkForceDelete([patient.id])"
              />
            </template>

            <template v-else-if="form.filter === 'deleted-patients'">
              <PatientsDeletedPatientRow
                v-for="(patient, index) in filteredPatients"
                :key="patient.id"
                :patient="patient"
                :test-id="getTestId(`${index}`)"
                :selected-rows="selectedRows"
                @update:selected-rows="(newSelectedRows) => (selectedRows = newSelectedRows)"
                @confirm:delete="() => confirmBulkForceDelete([patient.id])"
                @confirm:recover="() => confirmBulkRecover([patient.id])"
              />
            </template>

            <template v-else>
              <PatientsPatientRow
                v-for="(patient, index) in filteredPatients"
                :key="patient.id"
                :patient="patient"
                :test-id="getTestId(`${index}`)"
                :selected-rows="selectedRows"
                @update:selected-rows="(newSelectedRows) => (selectedRows = newSelectedRows)"
                @open:patient-note="openPatientNote(patient)"
                @open:patient-todos="openPatientTodos(patient)"
                @confirm:delete="() => confirmBulkDelete([patient.id])"
              />
            </template>
          </tbody>
        </table>

        <template v-else>
          <ComponentEmptyState v-if="form.q.search || form.flags.length > 0" :test-id="getTestId('search')">
            Für die aktuelle Suche / Filter wurden keine Patienten gefunden.
          </ComponentEmptyState>

          <ComponentEmptyState
            v-else-if="form.filter === 'shared-with-me' || form.filter === 'deleted-patients'"
            :test-id="getTestId('filter')"
          >
            <template v-if="form.filter === 'shared-with-me'">
              Es wurde kein Patient mit Ihnen geteilt, daher haben Sie keine externe Patienten.
            </template>

            <template v-else-if="form.filter === 'deleted-patients'">Sie haben keine gelöschten Patienten.</template>
          </ComponentEmptyState>

          <ComponentEmptyState v-else class="mr-10 max-w-152" :test-id="getTestId()">
            <div class="flex items-start">
              <div class="space-y-4">
                <p>
                  Legen Sie über den Button
                  <span class="font-semibold">„Neuer Patient“</span>
                  Ihren ersten Patienten an.
                </p>
              </div>

              <div class="relative -top-6 fill-blue-900">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  xml:space="preserve"
                  viewBox="0 0 100 100"
                  class="size-32 rotate-90"
                >
                  <path
                    d="M5.78 13.88c3.89-.42 7.89.35 11.76.77l11.91 1.32c1.31.14 2.82 3.76 1.07 3.78-.29 0-.57 0-.86.01-.2 0-.4-.06-.6-.16l-10.27-1.14c-2.27-.25-4.58-.62-6.89-.79 26.97 9.44 53.72 22.63 71.31 45.91 4.78 6.33 8.75 13.24 11.63 20.64.26.68.34 1.88-.6 2.03-.93.15-1.76-1.07-2.04-1.78-10.34-26.5-34.56-44.05-59.69-55.27-6.59-2.94-13.34-5.51-20.16-7.89 1.72 1.81 3.43 3.62 5.15 5.42.55.58 1.08 1.93.52 2.65-.61.8-1.59 0-2.06-.49-2.16-2.28-4.32-4.55-6.48-6.83-1.87-1.97-3.96-3.87-4.45-6.67-.1-.57.01-1.43.75-1.51z"
                  />
                </svg>
              </div>
            </div>
          </ComponentEmptyState>
        </template>

        <!-- Paginator -->
        <ComponentPagination v-if="!form.processing && patients.meta" :links="patients.meta.links" />
      </article>
      <!-- table listing :: end -->
    </div>
  </section>

  <ComponentPatientNoteDialog ref="refPatientNoteDialog" />

  <ComponentPatientTodosDialog ref="refPatientTodosDialog" :patients="patients" :test-id="getTestId()" />

  <!-- confirm delete -->
  <ComponentConfirmationDialog
    v-if="form.filter !== 'deleted-patients' && form.filter !== 'shared-with-me'"
    ref="refConfirmBulkDeleteDialog"
    :title="confirmDialogTitle"
    :content="confirmDialogContent"
    @confirmed="(patientIds) => bulkRemove(patientIds)"
  />

  <!-- confirm force delete -->
  <ComponentConfirmationDialog
    ref="refConfirmBulkForceDeleteDialog"
    :title="confirmDialogTitle"
    :content="confirmDialogContent"
    @confirmed="(patientIds) => bulkForceRemove(patientIds)"
  />

  <!-- confirm recover -->
  <ComponentConfirmationDialog
    v-if="form.filter === 'deleted-patients'"
    ref="refConfirmBulkRecoverDialog"
    :title="confirmDialogTitle"
    :content="confirmDialogContent"
    @confirmed="(patientIds) => bulkRecover(patientIds)"
  />
</template>
